import { Box, Button, Card, Typography } from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import { format } from 'date-fns';
import { useFormContext } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import FileCard from 'src/components/atoms/file-card/file-card';
import { Scrollbar } from 'src/components/atoms/scrollbar';
import { getTranslationBasedOnLanguage } from 'src/utils/get-translation-based-on-language';

import { PropertyList } from '../../../../../components/atoms/property-list';
import { PropertyListItem } from '../../../../../components/atoms/property-list-item';
import { CreateVisitFormValues } from '../../organisms/create-visit/schema/create-visit-validation-schema';

import { LoadingButton } from '@/components/atoms/loading-button/loading-button';
import { OrganizationLanguage } from '@/features/settings/api/types/gdpr';
import { Visit } from '@/features/visits/api/types/visits';
import { useAddAttachment } from '@/features/visits/hooks/mutation/use-add-attachment';
import { useCreateVisit } from '@/features/visits/hooks/mutation/use-create-visit';
import { useCategories } from '@/features/visits/hooks/query/use-categories';
import {
  CreateVisitContext,
  CreateVisitEvent,
} from '@/features/visits/utils/machine/create-visit-machine';
import { useKey } from '@/hooks/use-key';
import VisitorsTable from '../../organisms/create-visit/visitors-table';

interface Props {
  context: CreateVisitContext;
  send: (event: CreateVisitEvent) => void;
}
export const Confirmation = ({ context, send }: Props) => {
  const { t, i18n } = useTranslation(['visit', 'common', 'createVisit']);

  const navigate = useNavigate({ from: '/visits/create' });

  const { watch, handleSubmit } = useFormContext<CreateVisitFormValues>();

  const categoryId = watch('categoryId');
  const visitors = watch('visitors');
  const expectedAt = watch('expectedAt');
  const finishesAt = watch('finishesAt');
  const destinations = watch('destinations');
  const host = watch('host');
  const description = watch('description');
  const attachments = watch('attachments');
  const visitFinishSetting = watch('visitFinishSetting');

  const { data: categoriesData } = useCategories();

  const selectedCategory = categoriesData?.find(category => category.id === categoryId);

  const { mutate: addAttachment } = useAddAttachment();

  const onSuccessCreateVisit = async (data: Visit) => {
    try {
      const addAttachmentMutations = attachments?.map(file =>
        addAttachment({ visitId: data.id, file }),
      );

      await Promise.allSettled(addAttachmentMutations).then(results => {
        const statuses = results.map(result => result.status);
        const hasRejected = statuses.includes('rejected');
        const hasFulfilled = statuses.includes('fulfilled');

        if (hasRejected && !hasFulfilled) {
          toast.error(t('visit:attachments.dialog.add.failed'));
        }
        if (hasRejected && hasFulfilled) {
          toast.error(t('visit:attachments.dialog.add.warning'));
        }
        if (!hasRejected && hasFulfilled) {
          toast.success(t('visit:attachments.dialog.add.success'));
        }
      });
      navigate({ to: '/visits' });
    } catch (error) {
      console.error(error);
    }
  };

  const { mutate: createVisit, isPending } = useCreateVisit(onSuccessCreateVisit);

  const onSubmit = (data: CreateVisitFormValues) => {
    const visitors = data.visitors.map(({ localId, discountTemplateId, ...visitor }) => ({
      ...visitor,
      ...(discountTemplateId === null ? {} : { discountTemplateId }),
    }));

    createVisit({
      ...data,
      destinationIds: data.destinations.map(destination => destination.id),
      hostId: data.host?.id || null,
      visitors,
      expectedAt: data.expectedAt.toISOString(),
      finishesAt: data.finishesAt.toISOString(),
    });
  };

  const submit = handleSubmit(onSubmit);

  useKey('Enter', submit, !isPending);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography
        color="textPrimary"
        sx={{
          mb: 5,
          mt: 2,
        }}
        variant="h6">
        {t('common:step')} {context.step}. {t(context.content)}
      </Typography>

      <Card variant="outlined" sx={{ mt: 3 }}>
        <PropertyList>
          <PropertyListItem
            divider
            label={t('createVisit:steps.host')}
            value={host?.name || t('common:notAdded')}
          />
          <PropertyListItem
            divider
            label={t('createVisit:destination.destination')}
            value={destinations.map(dest => dest.name).join(', ')}
          />
          <PropertyListItem
            divider
            label={t('createVisit:dates.expectedAt')}
            value={format(expectedAt, 'dd-MM-yyyy HH:mm')}
          />
          <PropertyListItem
            divider
            label={t('createVisit:dates.finishesAt')}
            value={format(finishesAt, 'dd-MM-yyyy HH:mm')}
          />
          <PropertyListItem
            divider
            label={t('createVisit:additionalInformation.description')}
            value={description ? description : t('common:notAdded')}
          />
          <PropertyListItem
            divider
            label={t('createVisit:additionalInformation.categoryOfVisit')}
            value={
              selectedCategory
                ? getTranslationBasedOnLanguage(
                    selectedCategory?.translations,
                    i18n.language as OrganizationLanguage,
                  )
                : 'N/A'
            }
          />
          {visitFinishSetting ? (
            <PropertyListItem
              label={t('createVisit:additionalInformation.visitFinishSetting')}
              divider
              value={t(`createVisit:additionalInformation.${visitFinishSetting}`)}
            />
          ) : null}
          <PropertyListItem label={t('createVisit:additionalInformation.attachments')}>
            <Box sx={{ maxHeight: 200, width: '100%' }}>
              <Scrollbar>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    maxHeight: 180,
                  }}>
                  {attachments?.length > 0 ? (
                    attachments.map((file, index) => (
                      <FileCard key={`${file.name}-${index}`} path={file.name} size={file.size} />
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t('common:notAdded')}
                    </Typography>
                  )}
                </Box>
              </Scrollbar>{' '}
            </Box>
          </PropertyListItem>
        </PropertyList>
      </Card>

      <VisitorsTable visitors={visitors} discountTemplates={host?.discountTemplates ?? []} />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mt: 'auto',
          pt: 3,
        }}>
        <Button
          color="primary"
          onClick={() => send({ type: 'BACK' })}
          size="large"
          sx={{ mr: 2 }}
          type="button"
          variant="text">
          {t('common:button.back')}
        </Button>
        <LoadingButton
          loading={isPending}
          onClick={submit}
          color="primary"
          size="large"
          type="submit"
          variant="contained">
          {t('common:button.confirm')}
        </LoadingButton>
      </Box>
    </Box>
  );
};
